/**
 * ImpersonationBanner — persistent ribbon shown above all chrome whenever
 * the active session is a staff impersonation.
 *
 * Mounted once at the root layout level (above sidebar + header) so it
 * shows on every page including login redirects.
 */

import { useEffect, useState } from 'react'
import { ShieldAlert, X } from 'lucide-react'
import { useAuth } from '@/core/auth/useAuth'
import { useTranslation } from 'react-i18next'
import { Button } from '@/theme/components/ui/button'
import { cn } from '@/lib/utils'

export function ImpersonationBanner({ className }: { className?: string }) {
  const { impersonation, endImpersonation } = useAuth()
  const { t } = useTranslation()
  const [remaining, setRemaining] = useState<string>('')

  useEffect(() => {
    if (!impersonation) return
    function tick() {
      if (!impersonation) return
      const ms = new Date(impersonation.expiresAt).getTime() - Date.now()
      if (ms <= 0) {
        setRemaining(t('impersonation.banner.expired', 'expired'))
        return
      }
      const total = Math.floor(ms / 1000)
      const h = Math.floor(total / 3600)
      const m = Math.floor((total % 3600) / 60)
      const s = total % 60
      setRemaining(h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`)
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [impersonation, t])

  if (!impersonation) return null

  const scopeLabel =
    impersonation.scope === 'read_only'
      ? t('impersonation.banner.scope.readOnly', 'Read-only')
      : t('impersonation.banner.scope.full', 'Full access')

  return (
    <div
      role="alert"
      className={cn(
        'flex items-center gap-3 px-4 py-2 text-sm border-b',
        'bg-destructive text-destructive-foreground border-destructive',
        className,
      )}
    >
      <ShieldAlert className="h-4 w-4 shrink-0" />
      <p className="flex-1 min-w-0 flex items-baseline gap-2 flex-wrap">
        <span className="font-semibold uppercase tracking-wide text-xs">
          {t('impersonation.banner.title', 'Impersonation active')}
        </span>
        <span className="opacity-90">
          {t('impersonation.banner.summary', 'Initiated by {{by}} · {{scope}} · expires in {{remaining}}', {
            by: impersonation.impersonatingByEmail || impersonation.impersonatingBy,
            scope: scopeLabel,
            remaining,
          })}
        </span>
      </p>
      <Button
        size="sm"
        variant="outline"
        onClick={endImpersonation}
        className="bg-transparent border-destructive-foreground/40 text-destructive-foreground hover:bg-destructive-foreground/10"
      >
        <X className="h-3.5 w-3.5 mr-1.5" />
        {t('impersonation.banner.end', 'End impersonation')}
      </Button>
    </div>
  )
}
